import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  CalendarClock,
  Inbox as InboxIcon,
  Loader2,
  Mail,
  MessageSquareReply,
  Sparkles,
  UserRoundCheck,
  UserRoundX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';

type InboxBucket =
  | 'inbox'
  | 'unread_only'
  | 'reminders_only'
  | 'scheduled'
  | 'sent'
  | 'archived';
type ThreadStatus =
  | 'lead'
  | 'interested'
  | 'meeting_booked'
  | 'meeting_completed'
  | 'won'
  | 'archived';
type OverrideLabel =
  | 'interested'
  | 'meeting_intent'
  | 'out_of_office'
  | 'wrong_person'
  | 'unsubscribe'
  | 'needs_human';
type KnowledgeScope = 'workspace' | 'campaign';
type KnowledgeKind = 'faq' | 'objection' | 'policy' | 'product_note' | 'example_reply';

interface ThreadItem {
  id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  contact_id: string | null;
  contact_email: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_company_name: string | null;
  contact_position: string | null;
  installation_id: string | null;
  provider_thread_id: string | null;
  inbox_bucket: InboxBucket;
  current_status: ThreadStatus;
  latest_message_at: string | null;
  remind_at: string | null;
  reminder_note: string | null;
  created_at: string;
  updated_at: string;
  latest_inbound: {
    id: string;
    subject: string | null;
    preview: string;
    received_at: string | null;
    from_email: string | null;
  } | null;
  latest_classification: {
    label: string;
    score: number | null;
    rationale: string | null;
    suggested_action: string | null;
    remind_at: string | null;
    citations: Array<{ title?: string; snippet?: string }>;
  } | null;
  latest_draft: {
    id: string;
    status: string;
    tone: string | null;
    reply_text: string | null;
    requires_human_review?: boolean;
    auto_send_eligible?: boolean;
    suggested_action?: string | null;
    created_at: string;
  } | null;
}

interface ThreadListResponse {
  items: ThreadItem[];
  total: number;
  limit: number;
  offset: number;
  bucket_counts: Record<InboxBucket, number>;
  status_counts: Record<ThreadStatus, number>;
}

interface ThreadDetailResponse {
  thread: {
    id: string;
    workspace_id: string;
    campaign_id: string | null;
    campaign_name: string | null;
    campaign_code: string | null;
    contact_id: string | null;
    contact_email: string | null;
    contact_first_name: string | null;
    contact_last_name: string | null;
    contact_company_name: string | null;
    contact_company_domain: string | null;
    contact_position: string | null;
    installation_id: string | null;
    provider_thread_id: string | null;
    inbox_bucket: InboxBucket;
    current_status: ThreadStatus;
    latest_message_at: string | null;
    remind_at: string | null;
    reminder_note: string | null;
    offer_title: string | null;
    offer_description: string | null;
    sender_company_name: string | null;
    sender_first_name: string | null;
    sender_email: string | null;
    sender_role: string | null;
    reply_to_brand_handler: string | null;
    meeting_booking_handler: string | null;
    booking_enabled: boolean;
    provider_key: string | null;
    created_at: string;
    updated_at: string;
  };
  messages: Array<{
    id: string;
    direction: 'inbound' | 'outbound';
    subject: string | null;
    body_text: string | null;
    from_email: string | null;
    to_emails: string[];
    received_at: string | null;
    sent_at: string | null;
    created_at: string;
  }>;
  latest_classification: {
    id: string;
    label: string;
    score: number | null;
    rationale: string | null;
    suggested_action: string | null;
    remind_at: string | null;
    citations: Array<{ title?: string; snippet?: string }>;
    model_key: string | null;
  } | null;
  latest_draft: {
    id: string;
    status: string;
    tone: string | null;
    reply_text: string | null;
    reply_html: string | null;
    requires_human_review: boolean;
    auto_send_eligible: boolean;
    suggested_action: string | null;
    citations: Array<{ title?: string; snippet?: string }>;
    model_key: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  latest_meeting_booking: {
    id: string;
    status: string;
    form_slug: string | null;
    service_id: number | null;
    service_name: string | null;
    provider_id: number | null;
    provider_name: string | null;
    appointment_id: string | null;
    appointment_status: string | null;
    slot_date: string | null;
    start_hour: string | null;
    end_hour: string | null;
    timezone: string | null;
    summary: string | null;
    booked_at: string | null;
    extraction: Record<string, unknown>;
  } | null;
  knowledge_docs: Array<{
    id: string;
    campaign_id: string | null;
    scope: KnowledgeScope;
    kind: KnowledgeKind;
    title: string;
    content_md: string;
    active: boolean;
    updated_at: string;
  }>;
  suppression_matches: Array<{
    id: string;
    identity_type: string;
    identity_value: string;
    reason: string;
    source: string;
    source_ref: string | null;
    campaign_scope_id: string | null;
    created_at: string;
  }>;
}

interface MeetingBookingPreviewResponse {
  ok: boolean;
  thread_id: string;
  preview: {
    handler: 'getfishnet_r1';
    form_slug: string;
    form_name: string;
    routing_mode: string | null;
    timezone: string;
    service: { id: number; name: string | null };
    extraction: {
      matched_excerpt: string | null;
      requested_date: string | null;
      requested_start_hour: string | null;
      requested_end_hour: string | null;
      requested_period: 'morning' | 'afternoon' | 'evening' | null;
      timezone_hint: string | null;
      confidence: number;
      needs_confirmation: boolean;
      reason: string;
      source: 'llm' | 'heuristic';
      model: string | null;
    };
    suggestions: Array<{
      date: string;
      start_hour: string;
      end_hour: string | null;
      label: string;
      provider_ids: number[];
      recommended_provider_id: number | null;
      recommended_provider_name: string | null;
      in_preferred_window: boolean | null;
      match_type: 'exact' | 'date_match' | 'period_match' | 'fallback';
    }>;
  };
}

const BOOKING_MATCH_LABELS: Record<
  MeetingBookingPreviewResponse['preview']['suggestions'][number]['match_type'],
  string
> = {
  exact: 'Exact match',
  date_match: 'Same day',
  period_match: 'Preferred window',
  fallback: 'Suggested slot',
};

const BUCKET_LABELS: Record<InboxBucket, string> = {
  inbox: 'Inbox',
  unread_only: 'Unread',
  reminders_only: 'Reminders',
  scheduled: 'Scheduled',
  sent: 'Sent',
  archived: 'Archived',
};

const STATUS_LABELS: Record<ThreadStatus, string> = {
  lead: 'Lead',
  interested: 'Interested',
  meeting_booked: 'Meeting booked',
  meeting_completed: 'Meeting completed',
  won: 'Won',
  archived: 'Archived',
};

const LABEL_BADGE: Record<
  string,
  { tone: 'gray' | 'green' | 'red' | 'yellow' | 'blue' | 'purple' | 'brand'; label: string }
> = {
  interested: { tone: 'green', label: 'Interested' },
  not_interested: { tone: 'gray', label: 'Not interested' },
  unsubscribe: { tone: 'gray', label: 'Unsubscribe' },
  wrong_person: { tone: 'yellow', label: 'Wrong person' },
  out_of_office: { tone: 'blue', label: 'Out of office' },
  meeting_intent: { tone: 'purple', label: 'Meeting intent' },
  faq_answerable: { tone: 'brand', label: 'FAQ answerable' },
  needs_human: { tone: 'red', label: 'Needs human' },
};

const OVERRIDES: Array<{
  label: OverrideLabel;
  text: string;
  Icon: typeof UserRoundCheck;
}> = [
  { label: 'interested', text: 'Interested', Icon: UserRoundCheck },
  { label: 'meeting_intent', text: 'Meeting', Icon: CalendarClock },
  { label: 'out_of_office', text: 'OOO', Icon: Mail },
  { label: 'wrong_person', text: 'Wrong person', Icon: UserRoundX },
  { label: 'unsubscribe', text: 'Unsubscribe', Icon: Archive },
  { label: 'needs_human', text: 'Needs human', Icon: MessageSquareReply },
];

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toDateTimeLocalValue(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocalValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function formatMeetingSlot(
  date: string | null | undefined,
  startHour: string | null | undefined,
  endHour: string | null | undefined,
): string {
  if (!date || !startHour) return 'Slot not set';
  const parts = date.split('-');
  const year = Number(parts[0] ?? Number.NaN);
  const month = Number(parts[1] ?? Number.NaN);
  const day = Number(parts[2] ?? Number.NaN);
  const baseLabel =
    Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
      ? new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('fr-FR', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
        })
      : date;
  return `${baseLabel} - ${startHour}${endHour ? ` -> ${endHour}` : ''}`;
}

function personName(thread: ThreadItem | ThreadDetailResponse['thread'] | null): string {
  if (!thread) return 'No thread selected';
  const name = [thread.contact_first_name, thread.contact_last_name].filter(Boolean).join(' ');
  return name || thread.contact_email || thread.contact_company_name || 'Unknown contact';
}

function useInboxThreads(
  workspaceId: string | undefined,
  bucket: InboxBucket,
  status: ThreadStatus | '',
) {
  return useQuery<ThreadListResponse>({
    queryKey: ['inbox-threads', workspaceId, bucket, status],
    queryFn: async () => {
      const { data } = await apiClient.get<ThreadListResponse>('/v1/inbox/threads', {
        params: {
          workspace_id: workspaceId,
          bucket,
          ...(status ? { status } : {}),
          limit: 100,
        },
      });
      return data;
    },
    enabled: !!workspaceId,
    refetchInterval: 30_000,
  });
}

export default function InboxPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const queryClient = useQueryClient();

  const [bucket, setBucket] = useState<InboxBucket>('inbox');
  const [status, setStatus] = useState<ThreadStatus | ''>('');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [overrideNote, setOverrideNote] = useState('');
  const [docTitle, setDocTitle] = useState('');
  const [docContent, setDocContent] = useState('');
  const [docKind, setDocKind] = useState<KnowledgeKind>('faq');
  const [docScope, setDocScope] = useState<KnowledgeScope>('workspace');
  const [replyText, setReplyText] = useState('');
  const [replyHtml, setReplyHtml] = useState('');
  const [reminderAt, setReminderAt] = useState('');
  const [reminderNote, setReminderNote] = useState('');

  const threadsQuery = useInboxThreads(workspaceId, bucket, status);

  useEffect(() => {
    const firstThreadId = threadsQuery.data?.items[0]?.id ?? null;
    if (!selectedThreadId && firstThreadId) {
      setSelectedThreadId(firstThreadId);
      return;
    }

    if (
      selectedThreadId &&
      threadsQuery.data &&
      !threadsQuery.data.items.some((item) => item.id === selectedThreadId)
    ) {
      setSelectedThreadId(firstThreadId);
    }
  }, [threadsQuery.data, selectedThreadId]);

  const detailQuery = useQuery<ThreadDetailResponse>({
    queryKey: ['inbox-thread-detail', workspaceId, selectedThreadId],
    queryFn: async () => {
      const { data } = await apiClient.get<ThreadDetailResponse>(
        `/v1/inbox/threads/${selectedThreadId}`,
        {
          params: { workspace_id: workspaceId },
        },
      );
      return data;
    },
    enabled: !!workspaceId && !!selectedThreadId,
  });

  const currentThread = detailQuery.data?.thread ?? null;
  const latestMeetingIntent =
    detailQuery.data?.latest_classification?.label === 'meeting_intent';
  const shouldPrepareBookingPreview =
    Boolean(workspaceId) &&
    Boolean(selectedThreadId) &&
    Boolean(currentThread?.booking_enabled) &&
    latestMeetingIntent &&
    detailQuery.data?.latest_meeting_booking?.status !== 'booked';

  const bookingPreviewQuery = useQuery<MeetingBookingPreviewResponse>({
    queryKey: ['inbox-thread-booking-preview', workspaceId, selectedThreadId],
    queryFn: async () => {
      const { data } = await apiClient.post<MeetingBookingPreviewResponse>(
        `/v1/inbox/threads/${selectedThreadId}/r1-booking/preview`,
        {
          workspace_id: workspaceId,
        },
      );
      return data;
    },
    enabled: shouldPrepareBookingPreview,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (currentThread?.campaign_id) {
      setDocScope('campaign');
    } else {
      setDocScope('workspace');
    }
  }, [currentThread?.campaign_id]);

  useEffect(() => {
    setReplyText(detailQuery.data?.latest_draft?.reply_text ?? '');
    setReplyHtml(detailQuery.data?.latest_draft?.reply_html ?? '');
  }, [
    detailQuery.data?.latest_draft?.id,
    detailQuery.data?.latest_draft?.reply_text,
    detailQuery.data?.latest_draft?.reply_html,
  ]);

  useEffect(() => {
    setReminderAt(toDateTimeLocalValue(detailQuery.data?.thread.remind_at ?? null));
    setReminderNote(detailQuery.data?.thread.reminder_note ?? '');
  }, [detailQuery.data?.thread.id, detailQuery.data?.thread.remind_at, detailQuery.data?.thread.reminder_note]);

  const overrideMutation = useMutation({
    mutationFn: async (label: OverrideLabel) => {
      if (!workspaceId || !selectedThreadId) return;
      await apiClient.post(`/v1/inbox/threads/${selectedThreadId}/override`, {
        workspace_id: workspaceId,
        label,
        note: overrideNote.trim() || null,
        discard_existing_draft: label !== 'interested' && label !== 'meeting_intent',
      });
    },
    onSuccess: () => {
      toast.success('Override applied');
      setOverrideNote('');
      void queryClient.invalidateQueries({ queryKey: ['inbox-threads', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-thread-detail', workspaceId, selectedThreadId] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const knowledgeMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !docTitle.trim() || !docContent.trim()) return;
      await apiClient.post('/v1/inbox/knowledge-docs', {
        workspace_id: workspaceId,
        campaign_id: docScope === 'campaign' ? currentThread?.campaign_id ?? null : null,
        scope: docScope,
        kind: docKind,
        title: docTitle.trim(),
        content_md: docContent.trim(),
      });
    },
    onSuccess: () => {
      toast.success('Knowledge doc added');
      setDocTitle('');
      setDocContent('');
      void detailQuery.refetch();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !selectedThreadId) return;
      await apiClient.post(`/v1/inbox/threads/${selectedThreadId}/send-reply`, {
        workspace_id: workspaceId,
        draft_id: detailQuery.data?.latest_draft?.id ?? undefined,
        reply_text: replyText.trim() || undefined,
        reply_html: replyHtml.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Reply queued');
      void queryClient.invalidateQueries({ queryKey: ['inbox-threads', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-thread-detail', workspaceId, selectedThreadId] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const autoReplyMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !selectedThreadId || !detailQuery.data?.latest_draft?.id) return;
      await apiClient.post(`/v1/inbox/threads/${selectedThreadId}/auto-reply`, {
        workspace_id: workspaceId,
        draft_id: detailQuery.data.latest_draft.id,
      });
    },
    onSuccess: () => {
      toast.success('Auto-reply queued');
      void queryClient.invalidateQueries({ queryKey: ['inbox-threads', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-thread-detail', workspaceId, selectedThreadId] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const reminderMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !selectedThreadId) return;
      await apiClient.post(`/v1/inbox/threads/${selectedThreadId}/reminder`, {
        workspace_id: workspaceId,
        remind_at: fromDateTimeLocalValue(reminderAt),
        note: reminderNote.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Reminder saved');
      void queryClient.invalidateQueries({ queryKey: ['inbox-threads', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-thread-detail', workspaceId, selectedThreadId] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const bookMeetingMutation = useMutation({
    mutationFn: async (slot: MeetingBookingPreviewResponse['preview']['suggestions'][number]) => {
      if (!workspaceId || !selectedThreadId) return;
      await apiClient.post(`/v1/inbox/threads/${selectedThreadId}/r1-booking/book`, {
        workspace_id: workspaceId,
        date: slot.date,
        start_hour: slot.start_hour,
        timezone: bookingPreviewQuery.data?.preview.timezone,
        service_id: bookingPreviewQuery.data?.preview.service.id,
        ...(slot.recommended_provider_id ? { provider_id: slot.recommended_provider_id } : {}),
      });
    },
    onSuccess: () => {
      toast.success('R1 booked');
      void queryClient.invalidateQueries({ queryKey: ['inbox-threads', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-thread-detail', workspaceId, selectedThreadId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-thread-booking-preview', workspaceId, selectedThreadId] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const selectedSummary = useMemo(
    () => threadsQuery.data?.items.find((item) => item.id === selectedThreadId) ?? null,
    [threadsQuery.data?.items, selectedThreadId],
  );
  const latestMeetingBooking = detailQuery.data?.latest_meeting_booking ?? null;
  const meetingPreview = bookingPreviewQuery.data?.preview ?? null;

  if (!workspaceId) {
    return <div className="p-6 text-sm text-gray-500">Select a workspace to open the inbox.</div>;
  }

  return (
    <div className="h-full overflow-hidden px-6 py-5">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-dark">Inbox IA</h1>
          <p className="mt-1 text-sm text-gray-500">
            Classification locale, suggestions de reponse et base de connaissance pour {currentWorkspace?.name}.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            void threadsQuery.refetch();
            void detailQuery.refetch();
          }}
        >
          Refresh
        </Button>
      </div>

      <div className="grid h-[calc(100vh-12.5rem)] gap-4 lg:grid-cols-[220px_minmax(0,340px)_minmax(0,1fr)]">
        <aside className="overflow-y-auto border border-gray-200 bg-white px-4 py-4">
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-brand-dark">
              <InboxIcon size={15} />
              Buckets
            </div>
            <div className="space-y-1">
              {(Object.keys(BUCKET_LABELS) as InboxBucket[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBucket(value)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                    bucket === value ? 'bg-brand-primary/10 text-brand-primary' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{BUCKET_LABELS[value]}</span>
                  <span className="text-xs text-gray-400">
                    {threadsQuery.data?.bucket_counts[value] ?? 0}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <div className="mb-2 text-sm font-medium text-brand-dark">Status</div>
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setStatus('')}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                  status === '' ? 'bg-brand-primary/10 text-brand-primary' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>All</span>
                <span className="text-xs text-gray-400">
                  {Object.values(threadsQuery.data?.status_counts ?? {}).reduce((sum, value) => sum + value, 0)}
                </span>
              </button>
              {(Object.keys(STATUS_LABELS) as ThreadStatus[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatus(value)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                    status === value ? 'bg-brand-primary/10 text-brand-primary' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{STATUS_LABELS[value]}</span>
                  <span className="text-xs text-gray-400">
                    {threadsQuery.data?.status_counts[value] ?? 0}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4 text-xs text-gray-500">
            <p className="font-medium text-gray-700">Assistant rules</p>
            <p className="mt-2">
              Les suggestions restent locales. L'historique et les suppressions ne dependent pas du provider.
            </p>
          </div>
        </aside>

        <section className="overflow-y-auto border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-medium text-brand-dark">Threads</h2>
              <p className="text-xs text-gray-500">{threadsQuery.data?.total ?? 0} conversations</p>
            </div>
            {threadsQuery.isFetching && <Loader2 size={14} className="animate-spin text-gray-400" />}
          </div>

          <div className="divide-y divide-gray-100">
            {threadsQuery.isLoading && (
              <div className="p-6 text-sm text-gray-500">Loading inbox...</div>
            )}

            {!threadsQuery.isLoading && (threadsQuery.data?.items.length ?? 0) === 0 && (
              <div className="p-6 text-sm text-gray-500">No threads yet for this filter.</div>
            )}

            {threadsQuery.data?.items.map((thread) => {
              const classificationBadge = thread.latest_classification
                ? LABEL_BADGE[thread.latest_classification.label] ?? null
                : null;
              const selected = selectedThreadId === thread.id;
              return (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => setSelectedThreadId(thread.id)}
                  className={`block w-full px-4 py-4 text-left transition-colors ${
                    selected ? 'bg-brand-primary/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="mb-1 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-brand-dark">{personName(thread)}</div>
                      <div className="text-xs text-gray-500">
                        {thread.contact_company_name || thread.campaign_name || 'No campaign'}
                      </div>
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {formatDateTime(thread.latest_message_at)}
                    </div>
                  </div>

                  {thread.latest_inbound?.subject && (
                    <div className="mb-1 text-xs font-medium text-gray-700">
                      {thread.latest_inbound.subject}
                    </div>
                  )}
                  <div className="mb-2 text-xs leading-relaxed text-gray-500">
                    {thread.latest_inbound?.preview || 'No inbound preview'}
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge tone="gray">{BUCKET_LABELS[thread.inbox_bucket]}</Badge>
                    <Badge tone="blue">{STATUS_LABELS[thread.current_status]}</Badge>
                    {classificationBadge && (
                      <Badge tone={classificationBadge.tone}>{classificationBadge.label}</Badge>
                    )}
                    {thread.remind_at && <Badge tone="purple">Reminder</Badge>}
                    {thread.latest_draft && <Badge tone="brand">Draft</Badge>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="overflow-y-auto border border-gray-200 bg-white">
          {!selectedThreadId && (
            <div className="flex h-full items-center justify-center px-6 text-sm text-gray-500">
              Select a thread to inspect the assistant output.
            </div>
          )}

          {selectedThreadId && detailQuery.isLoading && (
            <div className="p-6 text-sm text-gray-500">Loading thread...</div>
          )}

          {detailQuery.data && (
            <div className="space-y-6 p-5">
              <div className="border-b border-gray-100 pb-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-brand-dark">
                      {personName(detailQuery.data.thread)}
                    </h2>
                    <div className="mt-1 text-sm text-gray-500">
                      {detailQuery.data.thread.contact_email || 'No email'}{' '}
                      {detailQuery.data.thread.contact_company_name
                        ? `- ${detailQuery.data.thread.contact_company_name}`
                        : ''}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {detailQuery.data.thread.campaign_name || 'No campaign'} -{' '}
                      {detailQuery.data.thread.contact_position || 'No role'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone="gray">{BUCKET_LABELS[detailQuery.data.thread.inbox_bucket]}</Badge>
                    <Badge tone="blue">{STATUS_LABELS[detailQuery.data.thread.current_status]}</Badge>
                    {detailQuery.data.thread.remind_at && (
                      <Badge tone="purple">Reminder {formatDateTime(detailQuery.data.thread.remind_at)}</Badge>
                    )}
                    {selectedSummary?.latest_draft && <Badge tone="brand">AI draft ready</Badge>}
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 px-4 py-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-brand-dark">
                  <Sparkles size={15} />
                  Assistant reading
                </div>
                {detailQuery.data.latest_classification ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {LABEL_BADGE[detailQuery.data.latest_classification.label] && (
                        <Badge tone={LABEL_BADGE[detailQuery.data.latest_classification.label]!.tone}>
                          {LABEL_BADGE[detailQuery.data.latest_classification.label]!.label}
                        </Badge>
                      )}
                      {detailQuery.data.latest_classification.score !== null && (
                        <span className="text-xs text-gray-500">
                          confidence {Math.round(detailQuery.data.latest_classification.score * 100)}%
                        </span>
                      )}
                      {detailQuery.data.latest_classification.remind_at && (
                        <span className="text-xs text-blue-700">
                          remind {formatDateTime(detailQuery.data.latest_classification.remind_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-gray-700">
                      {detailQuery.data.latest_classification.rationale}
                    </p>
                    {detailQuery.data.latest_classification.citations.length > 0 && (
                      <ul className="space-y-2 text-xs text-gray-500">
                        {detailQuery.data.latest_classification.citations.map((citation, index) => (
                          <li key={`${citation.title ?? 'citation'}-${index}`} className="border-l-2 border-gray-200 pl-3">
                            <div className="font-medium text-gray-700">{citation.title ?? 'Knowledge'}</div>
                            <div>{citation.snippet ?? ''}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No classification yet on this thread.</p>
                )}
              </div>

              {(currentThread?.booking_enabled || latestMeetingBooking) && (
                <div className="border border-gray-200 px-4 py-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-brand-dark">
                      <CalendarClock size={15} />
                      Booking R1
                    </div>
                    {shouldPrepareBookingPreview && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => void bookingPreviewQuery.refetch()}
                        loading={bookingPreviewQuery.isFetching}
                      >
                        Refresh slots
                      </Button>
                    )}
                  </div>

                  {latestMeetingBooking?.status === 'booked' ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone="green">Booked</Badge>
                        {latestMeetingBooking.provider_name && (
                          <Badge tone="brand">{latestMeetingBooking.provider_name}</Badge>
                        )}
                        {latestMeetingBooking.appointment_id && (
                          <span className="text-xs text-gray-500">
                            appointment {latestMeetingBooking.appointment_id}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700">
                        {latestMeetingBooking.summary ||
                          formatMeetingSlot(
                            latestMeetingBooking.slot_date,
                            latestMeetingBooking.start_hour,
                            latestMeetingBooking.end_hour,
                          )}
                      </div>
                      <div className="grid gap-3 text-xs text-gray-500 sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <div className="font-medium text-gray-700">Form</div>
                          <div>{latestMeetingBooking.form_slug || 'r1-mail'}</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-700">Service</div>
                          <div>{latestMeetingBooking.service_name || latestMeetingBooking.service_id || '-'}</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-700">Timezone</div>
                          <div>{latestMeetingBooking.timezone || '-'}</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-700">Booked at</div>
                          <div>{formatDateTime(latestMeetingBooking.booked_at)}</div>
                        </div>
                      </div>
                    </div>
                  ) : !latestMeetingIntent ? (
                    <p className="text-sm text-gray-500">
                      Ce thread n&apos;est pas encore qualifie comme intention de rendez-vous.
                    </p>
                  ) : bookingPreviewQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 size={14} className="animate-spin" />
                      Analyse du message et recherche des disponibilites Appointly...
                    </div>
                  ) : bookingPreviewQuery.isError ? (
                    <div className="space-y-2">
                      <p className="text-sm text-red-600">{getApiErrorMessage(bookingPreviewQuery.error)}</p>
                      <p className="text-xs text-gray-500">
                        Verifie la config GetFishnet ou relance un refresh une fois les secrets en place.
                      </p>
                    </div>
                  ) : meetingPreview ? (
                    <div className="space-y-4">
                      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
                        <div className="border border-gray-100 px-3 py-3">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <Badge tone="purple">Meeting intent</Badge>
                            <Badge tone="gray">{meetingPreview.form_name}</Badge>
                            <Badge tone="brand">{meetingPreview.service.name || `Service ${meetingPreview.service.id}`}</Badge>
                          </div>
                          <div className="text-sm leading-relaxed text-gray-700">
                            {meetingPreview.extraction.matched_excerpt
                              ? `"${meetingPreview.extraction.matched_excerpt}"`
                              : 'Aucun extrait precis detecte dans la reponse.'}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span>confidence {Math.round(meetingPreview.extraction.confidence * 100)}%</span>
                            <span>{meetingPreview.extraction.source === 'llm' ? 'LLM extraction' : 'heuristic extraction'}</span>
                            {meetingPreview.extraction.requested_date && (
                              <span>{meetingPreview.extraction.requested_date}</span>
                            )}
                            {meetingPreview.extraction.requested_start_hour && (
                              <span>{meetingPreview.extraction.requested_start_hour}</span>
                            )}
                            {meetingPreview.extraction.requested_period && (
                              <span>{meetingPreview.extraction.requested_period}</span>
                            )}
                          </div>
                        </div>

                        <div className="border border-gray-100 px-3 py-3 text-sm text-gray-700">
                          <div className="mb-2 font-medium text-brand-dark">Operator hint</div>
                          <p>{meetingPreview.extraction.reason || 'Choisis le meilleur creneau suggere, puis confirme en un clic.'}</p>
                          {meetingPreview.extraction.needs_confirmation && (
                            <p className="mt-2 text-xs text-amber-700">
                              Le message est partiellement ambigu. On propose les meilleurs creneaux compatibles.
                            </p>
                          )}
                        </div>
                      </div>

                      {meetingPreview.suggestions.length > 0 ? (
                        <div className="space-y-3">
                          {meetingPreview.suggestions.map((slot) => (
                            <div
                              key={`${slot.date}-${slot.start_hour}-${slot.recommended_provider_id ?? 'na'}`}
                              className="flex flex-col gap-3 border border-gray-100 px-3 py-3 lg:flex-row lg:items-center lg:justify-between"
                            >
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-sm font-medium text-gray-800">
                                    {formatMeetingSlot(slot.date, slot.start_hour, slot.end_hour)}
                                  </div>
                                  <Badge tone={slot.match_type === 'exact' ? 'green' : slot.match_type === 'fallback' ? 'gray' : 'blue'}>
                                    {BOOKING_MATCH_LABELS[slot.match_type]}
                                  </Badge>
                                  {slot.in_preferred_window && <Badge tone="purple">Preferred window</Badge>}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                  {slot.recommended_provider_name
                                    ? `Provider suggere: ${slot.recommended_provider_name}`
                                    : slot.recommended_provider_id
                                      ? `Provider suggere #${slot.recommended_provider_id}`
                                      : 'Routing automatique Perfex/Appointly'}
                                </div>
                              </div>

                              <Button
                                type="button"
                                onClick={() => bookMeetingMutation.mutate(slot)}
                                loading={
                                  bookMeetingMutation.isPending &&
                                  bookMeetingMutation.variables?.date === slot.date &&
                                  bookMeetingMutation.variables?.start_hour === slot.start_hour
                                }
                              >
                                Book R1
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          Aucun creneau disponible n&apos;a ete propose pour l&apos;instant.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Pas encore de suggestion de booking pour ce thread.
                    </p>
                  )}
                </div>
              )}

              <div className="border border-gray-200 px-4 py-4">
                <div className="mb-3 text-sm font-medium text-brand-dark">Manual override</div>
                <div className="mb-3">
                  <Textarea
                    value={overrideNote}
                    onChange={(event) => setOverrideNote(event.target.value)}
                    rows={2}
                    placeholder="Optional note for the override"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {OVERRIDES.map(({ label, text, Icon }) => (
                    <Button
                      key={label}
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={overrideMutation.isPending}
                      onClick={() => overrideMutation.mutate(label)}
                    >
                      <Icon size={14} />
                      {text}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 px-4 py-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-brand-dark">
                  <MessageSquareReply size={15} />
                  Reply draft
                </div>
                {detailQuery.data.latest_draft ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <Badge tone="brand">{detailQuery.data.latest_draft.status}</Badge>
                      {detailQuery.data.latest_draft.tone && <span>tone {detailQuery.data.latest_draft.tone}</span>}
                      {detailQuery.data.latest_draft.requires_human_review ? (
                        <Badge tone="yellow">Human review</Badge>
                      ) : (
                        <Badge tone="green">Ready</Badge>
                      )}
                      {detailQuery.data.latest_draft.auto_send_eligible && (
                        <Badge tone="purple">Auto-reply eligible</Badge>
                      )}
                      <span>{formatDateTime(detailQuery.data.latest_draft.created_at)}</span>
                    </div>
                    <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="reply-text">Reply text</Label>
                          <Textarea
                            id="reply-text"
                            rows={10}
                            value={replyText}
                            onChange={(event) => setReplyText(event.target.value)}
                            placeholder="Edit the AI draft before sending."
                          />
                        </div>
                        <div>
                          <Label htmlFor="reply-html">Reply HTML (optional)</Label>
                          <Textarea
                            id="reply-html"
                            rows={4}
                            value={replyHtml}
                            onChange={(event) => setReplyHtml(event.target.value)}
                            placeholder="Optional HTML body override."
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            onClick={() => sendReplyMutation.mutate()}
                            loading={sendReplyMutation.isPending}
                            disabled={!replyText.trim()}
                          >
                            Send reply
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => autoReplyMutation.mutate()}
                            loading={autoReplyMutation.isPending}
                            disabled={!detailQuery.data.latest_draft.auto_send_eligible}
                          >
                            Auto-reply
                          </Button>
                        </div>
                      </div>

                      <div className="border border-gray-100 px-3 py-3">
                        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-brand-dark">
                          <CalendarClock size={15} />
                          Reminder
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="thread-reminder-at">When</Label>
                            <Input
                              id="thread-reminder-at"
                              type="datetime-local"
                              value={reminderAt}
                              onChange={(event) => setReminderAt(event.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="thread-reminder-note">Note</Label>
                            <Textarea
                              id="thread-reminder-note"
                              rows={4}
                              value={reminderNote}
                              onChange={(event) => setReminderNote(event.target.value)}
                              placeholder="Why are we coming back to this thread?"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => reminderMutation.mutate()}
                              loading={reminderMutation.isPending}
                            >
                              Save reminder
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => {
                                setReminderAt('');
                                setReminderNote('');
                              }}
                            >
                              Clear form
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {detailQuery.data.latest_draft.citations.length > 0 && (
                      <div className="space-y-2 text-xs text-gray-500">
                        {detailQuery.data.latest_draft.citations.map((citation, index) => (
                          <div key={`${citation.title ?? 'draft-citation'}-${index}`} className="border-l-2 border-gray-200 pl-3">
                            <div className="font-medium text-gray-700">{citation.title ?? 'Knowledge'}</div>
                            <div>{citation.snippet ?? ''}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">No draft yet for this thread.</p>
                    <div>
                      <Label htmlFor="manual-reply-text">Manual reply</Label>
                      <Textarea
                        id="manual-reply-text"
                        rows={8}
                        value={replyText}
                        onChange={(event) => setReplyText(event.target.value)}
                        placeholder="Write a manual reply if the assistant has not drafted one yet."
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => sendReplyMutation.mutate()}
                      loading={sendReplyMutation.isPending}
                      disabled={!replyText.trim()}
                    >
                      Send reply
                    </Button>
                    <div className="border border-gray-100 px-3 py-3">
                      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-brand-dark">
                        <CalendarClock size={15} />
                        Reminder
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="thread-reminder-at-empty">When</Label>
                          <Input
                            id="thread-reminder-at-empty"
                            type="datetime-local"
                            value={reminderAt}
                            onChange={(event) => setReminderAt(event.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="thread-reminder-note-empty">Note</Label>
                          <Textarea
                            id="thread-reminder-note-empty"
                            rows={4}
                            value={reminderNote}
                            onChange={(event) => setReminderNote(event.target.value)}
                            placeholder="Why are we coming back to this thread?"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => reminderMutation.mutate()}
                          loading={reminderMutation.isPending}
                        >
                          Save reminder
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 px-4 py-4">
                <div className="mb-3 text-sm font-medium text-brand-dark">Conversation</div>
                <div className="space-y-3">
                  {detailQuery.data.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`px-3 py-3 text-sm ${
                        message.direction === 'inbound'
                          ? 'bg-gray-50 text-gray-800'
                          : 'bg-brand-primary/5 text-gray-700'
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between gap-3 text-xs text-gray-500">
                        <span>{message.direction === 'inbound' ? 'Inbound' : 'Outbound'}</span>
                        <span>{formatDateTime(message.received_at || message.sent_at || message.created_at)}</span>
                      </div>
                      {message.subject && <div className="mb-1 font-medium">{message.subject}</div>}
                      <div className="whitespace-pre-wrap leading-relaxed">
                        {message.body_text || 'No plain-text body available.'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="border border-gray-200 px-4 py-4">
                  <div className="mb-3 text-sm font-medium text-brand-dark">Knowledge base</div>
                  <div className="space-y-3">
                    {detailQuery.data.knowledge_docs.length === 0 && (
                      <p className="text-sm text-gray-500">No knowledge docs yet for this scope.</p>
                    )}
                    {detailQuery.data.knowledge_docs.map((doc) => (
                      <div key={doc.id} className="border border-gray-100 px-3 py-3">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{doc.title}</span>
                          <Badge tone="gray">{doc.scope}</Badge>
                          <Badge tone="purple">{doc.kind}</Badge>
                        </div>
                        <div className="text-xs leading-relaxed text-gray-500">
                          {doc.content_md.length > 240
                            ? `${doc.content_md.slice(0, 237).trim()}...`
                            : doc.content_md}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-200 px-4 py-4">
                  <div className="mb-3 text-sm font-medium text-brand-dark">Add doc</div>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="kb-title">Title</Label>
                      <Input
                        id="kb-title"
                        value={docTitle}
                        onChange={(event) => setDocTitle(event.target.value)}
                        placeholder="Pricing FAQ"
                      />
                    </div>
                    <div>
                      <Label htmlFor="kb-scope">Scope</Label>
                      <Select
                        id="kb-scope"
                        value={docScope}
                        onChange={(event) => setDocScope(event.target.value as KnowledgeScope)}
                      >
                        <option value="workspace">Workspace</option>
                        <option value="campaign" disabled={!currentThread?.campaign_id}>
                          Campaign
                        </option>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="kb-kind">Kind</Label>
                      <Select
                        id="kb-kind"
                        value={docKind}
                        onChange={(event) => setDocKind(event.target.value as KnowledgeKind)}
                      >
                        <option value="faq">FAQ</option>
                        <option value="objection">Objection</option>
                        <option value="policy">Policy</option>
                        <option value="product_note">Product note</option>
                        <option value="example_reply">Example reply</option>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="kb-content">Content</Label>
                      <Textarea
                        id="kb-content"
                        rows={6}
                        value={docContent}
                        onChange={(event) => setDocContent(event.target.value)}
                        placeholder="Put the approved guidance, FAQ, or reply policy here."
                      />
                    </div>
                    <Button
                      type="button"
                      loading={knowledgeMutation.isPending}
                      onClick={() => knowledgeMutation.mutate()}
                      disabled={!docTitle.trim() || !docContent.trim()}
                    >
                      Save doc
                    </Button>
                  </div>
                </div>
              </div>

              {detailQuery.data.suppression_matches.length > 0 && (
                <div className="border border-gray-200 px-4 py-4">
                  <div className="mb-3 text-sm font-medium text-brand-dark">Suppression matches</div>
                  <div className="space-y-2">
                    {detailQuery.data.suppression_matches.map((entry) => (
                      <div key={entry.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <Badge tone="gray">{entry.reason}</Badge>
                        <span>{entry.identity_value}</span>
                        <span className="text-gray-400">{entry.source}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
